import type { User } from 'firebase/auth';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { filterAndSortNotes, noteService } from '@/services';
import type { Note } from '@/types';
import { useBlocks } from './useBlocks';

// Keep the fallback identity stable for memoization.
const EMPTY_NOTES: Note[] = [];

export function useNotes(user: User | null) {
  const queryClient = useQueryClient();
  const { blockedUids, blockedByUids } = useBlocks(user);
  const [retryKey, setRetryKey] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  // The Firestore listener fills this disabled query's cache.
  const { data } = useQuery<Note[]>({
    queryKey: ['notes', user?.uid],
    queryFn: () => EMPTY_NOTES,
    enabled: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!user) return;

    const unsubscribe = noteService.subscribeToActiveNotes(
      updatedNotes => {
        setError(null);
        queryClient.setQueryData(['notes', user.uid], updatedNotes);
      },
      err => setError(err)
    );

    return () => unsubscribe();
  }, [user, queryClient, retryKey]);

  const notes = data ?? EMPTY_NOTES;

  const { myNote, entries } = useMemo(
    () =>
      filterAndSortNotes(notes, {
        viewerUid: user?.uid ?? '',
        blockedUids,
        blockedByUids,
      }),
    [notes, user?.uid, blockedUids, blockedByUids]
  );

  const refetch = useCallback(() => {
    setError(null);
    setRetryKey(prev => prev + 1);
  }, []);

  return {
    myNote,
    entries,
    isLoading: Boolean(user && data === undefined && !error),
    isError: error != null,
    error,
    refetch,
  };
}
